import { useEffect, useState } from 'react'
import FeaturePicker from './FeaturePicker'
import ManualMappingCell from './ManualMappingCell'
import { TYPE_LABELS, centsToInput, parseOptionalInt, parsePriceCents, typeLabel } from '../lib/format'
import { useUpdateUnit } from '../lib/hooks'
import type { ExternalUnitsState } from '../lib/hooks'
import { groupByType } from '../lib/mappings'
import type { AdminUnit, AdminZone, Integration, UnitMapping } from '../lib/types'

interface Props {
  slug: string
  unit: AdminUnit
  unitTypes: string[]
  zones: AdminZone[]
  integrations: Integration[]
  external: Map<string, ExternalUnitsState>
  selected: boolean
  onSelect: (id: string, on: boolean) => void
  onMapping: (integrationId: string, unitId: string, mapping: UnitMapping | null) => void
}

function mappingValue(mapping: UnitMapping | undefined): string {
  if (mapping?.externalUnitId) return `unit:${mapping.externalUnitId}`
  if (mapping?.externalTypeId) return `type:${mapping.externalTypeId}`
  return ''
}

export default function UnitRow({ slug, unit, unitTypes, zones, integrations, external, selected, onSelect, onMapping }: Props) {
  const save = useUpdateUnit(slug)
  const [name, setName] = useState(unit.name ?? '')
  const [type, setType] = useState(unit.type)
  const [zone, setZone] = useState(unit.zone ?? '')
  const [features, setFeatures] = useState<string[]>(unit.features)
  const [maxPersons, setMaxPersons] = useState(unit.maxPersons === null ? '' : String(unit.maxPersons))
  const [price, setPrice] = useState(centsToInput(unit.indicativePriceCents))

  // Keep the row in step with what the API stored (a save answers with the unit, a refetch may change it).
  useEffect(() => setName(unit.name ?? ''), [unit.name])
  useEffect(() => setType(unit.type), [unit.type])
  useEffect(() => setZone(unit.zone ?? ''), [unit.zone])
  useEffect(() => setFeatures(unit.features), [unit.features])
  useEffect(() => setMaxPersons(unit.maxPersons === null ? '' : String(unit.maxPersons)), [unit.maxPersons])
  useEffect(() => setPrice(centsToInput(unit.indicativePriceCents)), [unit.indicativePriceCents])

  // Let "opgeslagen" fade instead of sticking to the row for the rest of the session.
  const { isSuccess, reset } = save
  useEffect(() => {
    if (!isSuccess) return
    const timer = window.setTimeout(reset, 2500)
    return () => window.clearTimeout(timer)
  }, [isSuccess, reset])

  const label = `plaats ${unit.number}`
  // The API tells us which types it accepts; the built-in list is only a fallback.
  const types = [...new Set([...(unitTypes.length > 0 ? unitTypes : Object.keys(TYPE_LABELS)), unit.type])]

  return (
    <tr className={selected ? 'selected' : undefined}>
      <td className="tight">
        <input
          type="checkbox"
          checked={selected}
          onChange={(event) => onSelect(unit.id, event.target.checked)}
          aria-label={`Selecteer ${label}`}
        />
      </td>
      <td className="cell-number">{unit.number}</td>
      <td>
        <input
          type="text"
          value={name}
          aria-label={`Naam van ${label}`}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            const next = name.trim()
            if (next !== (unit.name ?? '')) save.mutate({ id: unit.id, patch: { name: next === '' ? null : next } })
          }}
        />
      </td>
      <td>
        <select
          value={type}
          aria-label={`Type van ${label}`}
          onChange={(event) => {
            setType(event.target.value)
            save.mutate({ id: unit.id, patch: { type: event.target.value } })
          }}
        >
          {types.map((key) => (
            <option key={key} value={key}>
              {typeLabel(key)}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select
          value={zone}
          aria-label={`Veld van ${label}`}
          onChange={(event) => {
            setZone(event.target.value)
            save.mutate({ id: unit.id, patch: { zone: event.target.value === '' ? null : event.target.value } })
          }}
        >
          <option value="">—</option>
          {zones.map((item) => (
            <option key={item.id} value={item.name}>
              {item.name}
            </option>
          ))}
          {zone !== '' && !zones.some((item) => item.name === zone) && <option value={zone}>{zone}</option>}
        </select>
      </td>
      <td>
        <FeaturePicker
          value={features}
          label={`Kenmerken van ${label}`}
          onChange={(next) => {
            setFeatures(next)
            save.mutate({ id: unit.id, patch: { features: next } })
          }}
        />
      </td>
      <td className="num">
        <input
          type="text"
          inputMode="numeric"
          className="num"
          style={{ width: '4rem' }}
          value={maxPersons}
          aria-label={`Maximaal aantal personen van ${label}`}
          onChange={(event) => setMaxPersons(event.target.value)}
          onBlur={() => {
            const next = parseOptionalInt(maxPersons)
            if (next !== unit.maxPersons) save.mutate({ id: unit.id, patch: { maxPersons: next } })
          }}
        />
      </td>
      <td className="num">
        <input
          type="text"
          inputMode="decimal"
          className="num"
          style={{ width: '5.5rem' }}
          value={price}
          aria-label={`Richtprijs van ${label} in euro`}
          onChange={(event) => setPrice(event.target.value)}
          onBlur={() => {
            const next = parsePriceCents(price)
            if (next !== unit.indicativePriceCents) save.mutate({ id: unit.id, patch: { indicativePriceCents: next } })
          }}
        />
      </td>
      {integrations.map((integration) => {
        const state = external.get(integration.id)
        const mapping = unit.mappings[integration.id]
        const value = mappingValue(mapping)
        const options = state?.units ?? []
        const known = value === '' || options.some((item) => mappingValue({ externalUnitId: item.externalUnitId, externalTypeId: item.externalTypeId }) === value || `type:${item.externalTypeId ?? ''}` === value)

        if (state?.loading) {
          return (
            <td key={integration.id}>
              <span className="spinner" aria-label="Types ophalen" />
            </td>
          )
        }

        // No list from this booking system: the ids are free text, so let them be typed.
        if (options.length === 0) {
          return (
            <td key={integration.id}>
              <ManualMappingCell
                label={`${label} bij ${integration.label}`}
                mapping={mapping}
                onChange={(next) => onMapping(integration.id, unit.id, next)}
              />
            </td>
          )
        }

        return (
          <td key={integration.id}>
            <select
              value={value}
              aria-label={`Koppeling van ${label} met ${integration.label}`}
              onChange={(event) => {
                const raw = event.target.value
                if (raw === '') onMapping(integration.id, unit.id, null)
                else if (raw.startsWith('type:'))
                  onMapping(integration.id, unit.id, { externalUnitId: null, externalTypeId: raw.slice(5) })
                else {
                  const externalUnitId = raw.slice(5)
                  const match = options.find((item) => item.externalUnitId === externalUnitId)
                  onMapping(integration.id, unit.id, { externalUnitId, externalTypeId: match?.externalTypeId ?? null })
                }
              }}
            >
              <option value="">— niet gekoppeld</option>
              {!known && <option value={value}>{mapping?.externalUnitId ?? mapping?.externalTypeId} (niet in lijst)</option>}
              {groupByType(options).map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.units[0]?.externalTypeId && (
                    <option value={`type:${group.units[0].externalTypeId}`}>Hele type: {group.label}</option>
                  )}
                  {group.units.map((item) => (
                    <option key={item.externalUnitId} value={`unit:${item.externalUnitId}`}>
                      {item.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </td>
        )
      })}
      <td className="tight">
        {save.isPending && <span className="save-state">opslaan…</span>}
        {save.isError && (
          <span className="save-state error" title={save.error.message}>
            niet opgeslagen
          </span>
        )}
        {save.isSuccess && <span className="save-state saved">opgeslagen</span>}
      </td>
    </tr>
  )
}
